import { CHROME_EXTENSION_ID } from '../../config/env';
import { ExternalMessageType } from '../../enums/ChromeExtension/ExternalMessageType';

type RunCallback = (extensionId: string) => void;

export default class ChromeExtensionService {
  private enabled: boolean;

  /**
   * In Chrome, the `chrome` object is available in all web pages. However,
   * in other browsers that object is not exposed, therefore we need to
   * check if it exists.
   *
   * Checking for `chrome.runtime` is required because this is only
   * available if a chrome extension is externally connectable to the
   * app's domain. This is defined in the extension's manifest file, under
   * the "matches" config.
   *
   * Doing both checks here makes it easier for integration for the app,
   * since we don't have to worry the script would fail because of undefined
   * properties.
   *
   * @see https://developer.chrome.com/docs/extensions/mv3/manifest/externally_connectable/
   */
  constructor() {
    try {
      this.enabled = chrome?.runtime?.sendMessage !== undefined;
    } catch (error) {
      this.enabled = false;
    }
  }

  /**
   * Sends the idToken to Enable Us Chrome Extension as an external message.
   * This is only possible because the extension's manifest is externally
   * connectable to Enable Us domain. It's up to the extension background
   * script to handle the auth credentials properly.
   */
  public notifyAuthentication(accessToken: string | undefined) {
    this.run((extensionId) => {
      chrome.runtime.sendMessage(extensionId, {
        accessToken,
        type: ExternalMessageType.Authenticate,
      });
    });
  }

  /**
   * Sends a message to Enable Us Chrome Extension to notify the sign out
   * action. It's up to the extension background script to handle this
   * properly, by disposing the access token.
   */
  public notifySignOut() {
    this.run((extensionId) => {
      chrome.runtime.sendMessage(extensionId, {
        type: ExternalMessageType.SignOut,
      });
    });
  }

  public sendMessage(type: ExternalMessageType, payload: Record<string, any>) {
    this.run((extensionId) => {
      chrome.runtime.sendMessage(extensionId, {
        type,
        ...payload,
      });
    });
  }

  /**
   * Convenience helper method to check if the chrome extension is enabled
   * and the app can communicate with it throught the messaging API.
   */
  private run(callback: RunCallback) {
    const extensionId = CHROME_EXTENSION_ID;
    if (!(extensionId && this.enabled)) {
      return;
    }

    callback(extensionId);
  }
}
